import type { NetWorthChartDto, NetWorthDto } from "@gladis/shared";
import { formatEur } from "@gladis/shared";
import { EmptyState, StackedAreaChart } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import type { ReactNode } from "react";

import { SectionCard } from "../section-card.js";

/**
 * ③ The Net Worth Chart — the last twelve months of where the household stands.
 *
 * ==========================================================================================
 * THREE LAYERS, ALL POSITIVE, AND THE TOP OF THE STACK IS NET WORTH
 * ==========================================================================================
 *
 * It was FOUR — savings, investments, properties, and the mortgage drawn below the axis — and under
 * that shape the top of the stack was total ASSETS, with net worth read as the distance between the
 * stack's top and the band's floor. Correct, and hard: the figure the section is named after was a GAP
 * between two edges rather than an edge, and the household's largest asset sat separated from its
 * largest liability by the axis, so their difference had to be measured by eye at every month.
 *
 * The property and the mortgage are one layer now — EQUITY, what the household actually owns of its
 * houses — so all three bands are positive and the top of the stack is the figure on the card.
 *
 * WHAT THAT COSTS, AND WHERE IT IS PAID. A household that owes more on a property than it is worth has
 * negative equity, and a stacked area cannot draw a negative band inside a positive stack: with
 * `stackOffset="sign"` Recharts accumulates it onto the top and draws it downward from there — three
 * layers, no error, and not the household's position (research R3, in a new place). So the server
 * CLAMPS that layer at zero and names the months in `clampedMonths`, and this section prints a
 * sentence when it does. The summary card still shows the true, lower net worth; without the sentence
 * the two would disagree with nothing on screen to explain it, which is this page's oldest failure.
 *
 * BOTH COMPONENT FIGURES SURVIVE in the composition list below, where a number may be negative without
 * any of this arising. Nothing left the page; one picture stopped being two.
 *
 * ==========================================================================================
 * IT DERIVES NOTHING
 * ==========================================================================================
 *
 * The window, the months and every value arrive resolved: the carry-forward, the never-interpolate, the
 * month-end evaluation, the unpriced holding omitted rather than zeroed, and the mortgage's sign. The
 * series are DENSE for that reason — `chartPositions` builds the axis from the union of the points it is
 * handed, so a component that received a sparse layer would silently narrow the window and let
 * `valueAt` paint a month the server never computed (research R9).
 *
 * NO RANGE SELECTOR (FR-036). The savings chart has one because a household asks two different
 * questions of its accounts; this chart answers one question, and a control here would also invite the
 * reader to compare a range they picked against a card that is fixed at twelve months.
 */

/** Cents-as-string → euros, which is the unit `StackedAreaChart` formats and plots. */
function euros(cents: string): number {
  return Number(cents) / 100;
}

/**
 * A pillar's figure, and — for the three asset ones — its share beside it.
 *
 * One string rather than a second column, and `dashboard-layout.md`'s mockup shows exactly this shape
 * (`€412,000 · 78%`). MOVED HERE FROM THE CARD on 2026-08-14 with the rows it formats.
 */
function withShare(cents: string, percent: number | undefined): string {
  const figure = formatEur(euros(cents));
  return percent === undefined ? figure : `${figure} · ${percent}%`;
}

/**
 * WHAT NET WORTH IS MADE OF — the four pillars, their shares, and the loan-to-value ratio.
 *
 * ==========================================================================================
 * IT WAS ON THE CARD, AND IT BELONGS WITH THE CHART
 * ==========================================================================================
 *
 * The net worth card held six figures while the three cards beside it held one each, which made it a
 * different kind of object in a row whose whole job is to be scannable. The rows moved here on
 * 2026-08-14, and this is the better home for them independently of that: THIS CHART DRAWS THESE EXACT
 * FOUR LAYERS, so the list is the legend, and a reader comparing a share against a band is looking at
 * one section rather than two.
 *
 * AC-D20 AND AC-D21 ARE UNCHANGED. The three asset rows carry their share of total ASSETS; mortgages
 * carry none, because they are not part of the asset side and a share of a whole they do not belong to
 * is meaningless — the absence is the statement. The LTV is omitted entirely when the household owns no
 * property, never rendered as 0%, because a ratio over zero is not a percentage.
 */
function Composition({ netWorth }: { netWorth: NetWorthDto }): ReactNode {
  const t = useT();
  const shares = netWorth.assetShares;

  const rows = [
    {
      key: "savings",
      label: t("nav.savings"),
      value: withShare(netWorth.savingsCents, shares?.savingsPercent),
    },
    {
      key: "investments",
      label: t("nav.investments"),
      value: withShare(netWorth.investmentsCents, shares?.investmentsPercent),
    },
    {
      key: "properties",
      label: t("nav.properties"),
      value: withShare(netWorth.propertiesCents, shares?.propertiesPercent),
    },
    {
      /*
       * VEHICLES, MISSING FROM THIS LIST UNTIL 2026-08-22 THOUGH THE CHART HAS DRAWN THE BAND SINCE
       * 023.
       *
       * The list is the chart's legend, so a layer with no row here left one band unnamed — and the
       * headline it sat under was short by the same figure, because `PillarSubtotals` had no such
       * field either. Both halves are fixed together; `dashboard.test.ts` now owns a vehicle so the
       * assertion that the layers sum to the headline can tell the two apart.
       */
      key: "vehicles",
      label: t("veh.nav"),
      value: withShare(netWorth.vehiclesCents, shares?.vehiclesPercent),
    },
    {
      key: "mortgages",
      label: t("dash.pillar.mortgages"),
      /*
       * NEGATED HERE, AND NOW IN EXACTLY ONE PLACE. It used to be two — this row and the chart's own
       * mortgage layer — and the layer is gone: the server subtracts the loan into equity instead. The
       * server still sends a positive magnitude, precisely so a value cannot be signed twice by two
       * owners; the symptom would be the household's debt drawn as an asset, which reads as an
       * entirely plausible number.
       */
      value: formatEur(-euros(netWorth.mortgagesCents)),
    },
  ];

  /*
   * EQUITY, THE LAYER THE CHART DRAWS, SPELLED OUT BETWEEN ITS TWO PARTS.
   *
   * The list is the chart's legend, so the band has to appear in it — and the two rows above are what
   * it is made of, which is why they stay. A reader who wants to check the band reads property minus
   * mortgage on the two lines directly above it.
   *
   * COMPUTED HERE, IN EUROS, AND THAT IS SAFE ONLY BECAUSE IT IS A SUBTRACTION OF TWO FIGURES ALREADY
   * ON SCREEN. Every other number on this page arrives resolved from the server; this one is the
   * difference between two of them, rounded identically, so it cannot disagree with what is printed
   * above it. A share is NOT given: `assetShares` is a proportion of ASSETS, and equity is not one of
   * them — a percentage of a whole this figure does not belong to is meaningless, which is the same
   * reason the mortgage row has never carried one.
   *
   * It may be NEGATIVE here, and it is allowed to be. That is the whole reason the figure lives in a
   * list and the band lives in a chart: a number can go below zero and a stacked area cannot.
   */
  rows.push({
    key: "equity",
    label: t("dash.pillar.equity"),
    value: formatEur(euros(netWorth.propertiesCents) - euros(netWorth.mortgagesCents)),
  });

  if (netWorth.loanToValuePercent !== undefined) {
    rows.push({ key: "ltv", label: t("dash.ltv"), value: `${netWorth.loanToValuePercent}%` });
  }

  return (
    <dl className="mt-5 grid grid-cols-[repeat(auto-fit,minmax(11rem,1fr))] gap-x-6 gap-y-2 border-t border-line pt-4">
      {rows.map((row) => (
        <div key={row.key} className="flex items-baseline justify-between gap-3">
          <dt className="text-sm text-muted">{row.label}</dt>
          <dd className="text-sm font-medium tabular-nums text-ink">{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function NetWorthChart({
  chart,
  netWorth,
}: {
  chart: NetWorthChartDto;
  netWorth: NetWorthDto;
}): ReactNode {
  const t = useT();
  /*
   * Flattened across the layers rather than read off `equity` by name.
   *
   * The id is the server's to choose and only one layer can ever carry this, so asking "did anything
   * get clamped" is both the honest question and the one that keeps working if the layer is ever
   * renamed. It reads a field, not a position.
   */
  const clampedMonths = chart.series.flatMap((layer) => layer.clampedMonths ?? []);

  return (
    /*
      A NAMED SECTION IN A PANEL, like every other piece on the page (AC-D23's neighbour, SC-013). A
      chart that is only an `<svg>` is one unlabelled region in a screen reader's outline between two
      labelled ones — and, unframed, it did not read as a section to a sighted reader either.

      THE TITLE NAMES THE SUBJECT. It was "Last 12 months", which names the window and leaves the
      reader to infer what is being charted from the card above it. The window moved to the hint, where
      it qualifies the figures rather than standing in for their name.
    */
    <SectionCard
      title={t("dash.chart.heading")}
      hint={t("dash.chart.hint")}
      testId="net-worth-chart"
    >
      {chart.months.length < 2 ? (
        /*
         * ONE MONTH DRAWS NOTHING AT ALL, so it says so instead.
         *
         * The window is floored at the earlier of household creation and the household's oldest record,
         * so a household that onboarded this morning and typed in today's balances has a single month.
         * An area needs two points to have any width: Recharts emits no path, and the section renders as
         * an empty box the height of a chart — which reads as a broken chart rather than as a young
         * household. `isEmpty` inside the primitive cannot catch this, because there IS a point.
         *
         * The figures are all on the card above; what is missing is history, and history is a matter of
         * time rather than of anything the member can do now. So this says that and nothing more.
         */
        <EmptyState>{t("dash.chart.oneMonth")}</EmptyState>
      ) : (
        <StackedAreaChart
          series={chart.series.map((layer) => ({
            id: layer.id,
            label: layer.label,
            points: layer.points.map((point) => ({ at: point.at, value: euros(point.value) })),
          }))}
          variant="stacked"
          /*
           * THE TOTAL LINE STAYS, AND IT IS NOW THE STACK'S OWN TOP EDGE.
           *
           * It was added because the four-layer shape did not draw net worth anywhere: the top of the
           * stack was total ASSETS and net worth was the gap down to the mortgage band's floor. With
           * three positive layers the sum of the bands IS net worth, so the line coincides with the
           * silhouette — which is not redundant. It carries the label and the tooltip entry for the
           * figure the section is named after, and it is what makes the top edge legible as a value
           * rather than as wherever the last band happens to end.
           *
           * The primitive sums the layers it is drawing rather than taking a total from here, so the
           * line cannot disagree with the areas underneath it. On a month where equity was CLAMPED it
           * therefore reads high — by exactly the amount the sentence below names.
           */
          total={{ label: t("dash.netWorth") }}
          /*
           * NO BUILT-IN LEGEND: the composition below IS the legend, and it carries each pillar's figure
           * and its share of assets rather than just a colour and a name. Both rendered put "Savings" on
           * screen twice in one card.
           */
          legend={false}
          emptyMessage={t("dash.chart.empty")}
        />
      )}

      {/*
        THE ONE THING A CLAMPED BAND CANNOT SAY FOR ITSELF.

        Negative equity is drawn as zero because a stacked area has no way to draw it — so on those
        months the plot shows a floor the household is not standing on, and the summary card above
        prints the true, lower net worth. Money missing from a picture with no error is the failure
        this page guards hardest against, so it is stated in text, in the section that did it.

        `text-muted` AND NOT `text-danger`, WHICH IS A DELIBERATE CHOICE AND NOT THE OBVIOUS ONE. The
        expenses list colours its equivalent — "the mortgage could not be read, so this total is short" —
        and this page may not: `dashboard-accessibility.test.ts` bans every status colour here, because
        AC-D23 says no figure on the dashboard may depend on one, and this would have been the first.

        Nothing is lost by obeying it. The sentence states the whole fact in words, which is what that
        criterion asks for everywhere else on the page; colour would have added emphasis and no
        information. It sits directly under the plot it is about, which is the emphasis that matters.
      */}
      {clampedMonths.length > 0 ? (
        <p className="mt-4 text-sm text-muted">
          {t("dash.chart.equityClamped", { count: clampedMonths.length })}
        </p>
      ) : null}

      {/* Below the plot rather than above it: the chart is the section's subject and this is what it is
          made of, so the reader meets the shape first and the figures when they want them. */}
      <Composition netWorth={netWorth} />
    </SectionCard>
  );
}
